/**
 * Normalised API errors.
 *
 * The backend error envelope is authoritative (PO 7.3 §12):
 * `{ error: { code, message, correlationId, details?: [{key, code, message}] } }`, with the HTTP
 * status carrying the kind (400 shape · 401 session · 403 authorization · 404 · 409 lifecycle ·
 * 410 claim token · 422 domain validation · 429 rate limit · 500). Every screen reacts to
 * `code`, never to a parsed message, and a meaningful code is never flattened into
 * "Bir hata oluştu."
 *
 * Nothing user-visible here contains a stack trace, SQL, a provider error, an internal id or a
 * token: the backend already refuses to send those, and the fallbacks below do not invent them.
 */

export type ApiErrorKind =
  | 'network' // request never reached the backend (offline, DNS, CORS preflight, abort)
  | 'malformed' // 400
  | 'unauthenticated' // 401
  | 'unauthorized' // 403
  | 'not_found' // 404
  | 'conflict' // 409
  | 'gone' // 410
  | 'validation' // 422
  | 'rate_limited' // 429
  | 'server'; // 5xx and anything unclassifiable

export interface FieldIssue {
  key: string;
  code: string;
  message: string;
}

export class ApiError extends Error {
  readonly kind: ApiErrorKind;
  readonly code: string;
  readonly status: number;
  readonly correlationId: string | null;
  readonly details: FieldIssue[];

  constructor(init: {
    kind: ApiErrorKind;
    code: string;
    message: string;
    status: number;
    correlationId?: string | null;
    details?: FieldIssue[];
  }) {
    super(init.message);
    this.name = 'ApiError';
    this.kind = init.kind;
    this.code = init.code;
    this.status = init.status;
    this.correlationId = init.correlationId ?? null;
    this.details = init.details ?? [];
  }

  /** True when re-authenticating could plausibly fix it. */
  get needsSignIn(): boolean {
    return this.kind === 'unauthenticated';
  }

  get isForbidden(): boolean {
    return this.kind === 'unauthorized';
  }
}

const KIND_BY_STATUS: Record<number, ApiErrorKind> = {
  400: 'malformed',
  401: 'unauthenticated',
  403: 'unauthorized',
  404: 'not_found',
  409: 'conflict',
  410: 'gone',
  413: 'malformed',
  415: 'malformed',
  422: 'validation',
  429: 'rate_limited',
};

export function kindForStatus(status: number): ApiErrorKind {
  return KIND_BY_STATUS[status] ?? (status >= 500 ? 'server' : 'server');
}

interface RawEnvelope {
  error?: {
    code?: unknown;
    message?: unknown;
    correlationId?: unknown;
    details?: unknown;
  };
}

function readDetails(value: unknown): FieldIssue[] {
  if (!Array.isArray(value)) return [];
  const out: FieldIssue[] = [];
  for (const item of value) {
    if (!item || typeof item !== 'object') continue;
    const d = item as { key?: unknown; code?: unknown; message?: unknown };
    out.push({
      key: typeof d.key === 'string' ? d.key : '',
      code: typeof d.code === 'string' ? d.code : 'invalid',
      message: typeof d.message === 'string' ? d.message : '',
    });
  }
  return out;
}

/** Builds an ApiError from a response the backend already shaped. */
export function apiErrorFromEnvelope(status: number, body: unknown): ApiError {
  const envelope = (body ?? {}) as RawEnvelope;
  const error = envelope.error ?? {};
  const code = typeof error.code === 'string' && error.code ? error.code : `http_${status}`;
  const message = typeof error.message === 'string' ? error.message : '';
  return new ApiError({
    kind: kindForStatus(status),
    code,
    message: message || code,
    status,
    correlationId: typeof error.correlationId === 'string' ? error.correlationId : null,
    details: readDetails(error.details),
  });
}

export function networkError(cause: unknown): ApiError {
  const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
  return new ApiError({
    kind: 'network',
    code: offline ? 'offline' : 'network_unreachable',
    message: cause instanceof Error ? cause.message : 'network request failed',
    status: 0,
  });
}

export function isApiError(e: unknown): e is ApiError {
  return e instanceof ApiError;
}

/**
 * User-facing Turkish text per backend code. The admin surface of the frozen prototype is
 * Turkish-only, so these are the admin strings; the public and claim surfaces carry their own
 * bilingual dictionaries (src/i18n).
 *
 * Anything not listed falls back to a code-specific but still safe sentence — never a provider
 * message, never a stack, and never the bare "Bir hata oluştu."
 */
const MESSAGES: Record<string, string> = {
  // session / authorization
  unauthenticated: 'Oturumunuz bulunamadı. Yeniden giriş yapın.',
  invalid_session: 'Oturumunuz geçersiz. Yeniden giriş yapın.',
  session_expired: 'Oturum süresi doldu. Yeniden giriş yapın.',
  unknown_internal_user: 'Bu hesap sistemde tanımlı değil. Bir yönetici sizi eklemelidir.',
  /*
   * One sentence for every refusal the first-owner setup can give, because the server gives one
   * answer for all of them (PO 7.10 §7). Guessing at «an owner already exists» or «this is the
   * wrong account» here would put back exactly the information the uniform answer withholds.
   */
  bootstrap_unavailable:
    'İlk yönetici kurulumu bu oturum için yapılamadı. Kurulum kapalı olabilir, sistemde zaten bir yönetici tanımlı olabilir ya da bu hesap kurulum için tanımlı olmayabilir. Hiçbir kayıt oluşturulmadı.',
  forbidden: 'Bu işlem için yetkiniz yok.',
  no_active_role: 'Hesabınızın aktif bir rolü yok. Bir yönetici rol atamalıdır.',
  release_not_published: 'Yalnız yayınlanmış bir release ile işlem yapılabilir.',
  identity_change_forbidden: 'Kimlik alanları düzeltme ile değiştirilemez; iptal edip yeniden düzenleyin.',
  asset_referenced_immutable: 'Bu görsel bir şablonda kullanılıyor; silinemez.',
  condition_context_missing: 'Bu işlem için gereken bağlam eksik.',
  // shape / validation
  schema_validation_failed: 'Gönderilen bilgiler geçersiz. İşaretli alanları düzeltin.',
  malformed_input: 'İstek okunamadı.',
  payload_too_large: 'Dosya ya da istek çok büyük.',
  unsupported_media_type: 'Bu dosya türü kabul edilmiyor.',
  binary_body_required: 'Dosya içeriği gönderilmedi.',
  issuance_validation_failed: 'Kayıt üretilemedi: alan doğrulaması başarısız.',
  validation_failed: 'Alan doğrulaması başarısız.',
  reason_required: 'Gerekçe zorunludur (en az 3 karakter).',
  actor_required: 'İşlemi yapan kişi belirtilmelidir.',
  recipient_email_required: 'Alıcı e-posta adresi gerekli.',
  revoke_reason_unknown: 'İptal gerekçesi tanınmadı.',
  revoke_note_required: '«Diğer» gerekçesi için not zorunludur.',
  correction_reason_unknown: 'Düzeltme gerekçesi tanınmadı.',
  correction_note_required: '«Diğer» gerekçesi için not zorunludur.',
  no_change: 'Değişiklik yok.',
  invalid_email: 'E-posta adresi geçersiz.',
  max_rows_invalid: 'Satır sınırı geçersiz.',
  audit_filter_required: 'Denetim kaydı için correlationId ya da tür filtresi verin.',
  // idempotency
  idempotency_key_required: 'Bu işlem bir idempotency anahtarı gerektirir.',
  idempotency_in_flight: 'Aynı işlem hâlâ sürüyor. Sonucu bekleyin.',
  idempotency_key_reused_with_different_request:
    'Bu anahtar farklı bir istekle kullanılmış. Yeni bir işlem başlatın.',
  // lifecycle
  wrong_state: 'Kaydın durumu bu işleme uygun değil.',
  invalid_lifecycle_transition: 'Bu durum geçişi tanımlı değil.',
  expired_credential: 'Kaydın geçerlilik süresi dolduğu için bu işlem yapılamaz.',
  stale_revision: 'Kayıt siz görüntülerken değişti. Sayfayı yenileyip yeniden deneyin.',
  publication_policy_mismatch: 'Bu kayıt alıcı onayı akışında değil.',
  supersession_link_inconsistent: 'Kayıt bağlantısı tutarsız kaldı; işlem geri alındı.',
  // templates / releases / assets
  template_not_found: 'Şablon bulunamadı.',
  template_archived: 'Arşivlenmiş şablonla işlem yapılamaz.',
  template_has_releases: 'Yayınlanmış şablonun taslağı silinemez; arşivleyin.',
  draft_not_found: 'Şablon taslağı bulunamadı.',
  publish_blocked: 'Yayın kapısı geçilemedi. Kontrol listesindeki maddeleri tamamlayın.',
  release_not_found: 'Release bulunamadı.',
  asset_not_found: 'Görsel bulunamadı.',
  font_not_found: 'Font bulunamadı.',
  license_acknowledgement_required: 'Font lisans onayı gerekli.',
  malware_scan_required: 'Dosya taranamadı; yükleme kabul edilmedi.',
  scan_infected: 'Dosya güvenli bulunmadı.',
  // credentials
  credential_not_found: 'Kayıt bulunamadı.',
  public_code_unknown: 'Bu kodla eşleşen bir kayıt yok.',
  public_code_ambiguous_collision: 'Kod tek bir kayda çözülemedi.',
  public_code_mint_failed: 'Public kod üretilemedi. Yeniden deneyin.',
  download_not_permitted: 'Bu kayıt için indirme açık değil.',
  artifact_not_found: 'Bu kayıt için görsel bulunamadı.',
  storage_integrity_violation: 'Görsel bütünlüğü doğrulanamadı; dosya sunulmadı.',
  // claim
  claim_token_unknown: 'Onay bağlantısı tanınmadı.',
  claim_token_consumed: 'Bu onay bağlantısı daha önce kullanıldı.',
  claim_token_revoked: 'Bu onay bağlantısı geçersiz kılınmış.',
  claim_token_expired: 'Bu onay bağlantısının süresi doldu.',
  // batch
  batch_not_found: 'Yükleme bulunamadı.',
  batch_file_empty: 'Dosya boş.',
  batch_file_too_large: 'Dosya izin verilen boyuttan büyük.',
  batch_format_mismatch: 'Dosya biçimi seçilen türle uyuşmuyor.',
  batch_not_editable: 'Bu yükleme artık düzenlenemez.',
  batch_not_planned: 'Önce doğrulama (dry-run) çalıştırın.',
  batch_already_committed: 'Bu yükleme zaten işlendi; ikinci kez üretilmez.',
  batch_release_changed: 'Şablon release değişti; doğrulamayı yeniden çalıştırın.',
  mapping_invalid: 'Kolon eşlemesi geçersiz.',
  table_empty: 'Dosyada satır yok.',
  table_malformed: 'Dosya okunamadı (biçim bozuk).',
  table_too_many_rows: 'Dosyadaki satır sayısı sınırın üzerinde.',
  table_too_many_columns: 'Dosyadaki kolon sayısı sınırın üzerinde.',
  table_cell_too_long: 'Bir hücre çok uzun.',
  table_encoding_invalid: 'Dosya kodlaması UTF-8 değil.',
  table_header_invalid: 'Başlık satırı geçersiz.',
  // users
  user_not_found: 'Kullanıcı bulunamadı.',
  owner_already_exists: 'Zaten bir aktif owner var.',
  unknown_role: 'Rol tanınmadı.',
  role_unchanged: 'Rol aynı; değişiklik yapılmadı.',
  multiple_active_roles: 'Kullanıcının birden fazla aktif rolü var.',
  last_owner_protected: 'Son aktif owner devre dışı bırakılamaz.',
  // transport
  rate_limited: 'Çok fazla istek gönderildi. Kısa bir süre sonra yeniden deneyin.',
  offline: 'Bağlantı yok. İnternet bağlantınızı kontrol edin.',
  network_unreachable: 'Sunucuya ulaşılamadı. Bağlantınızı kontrol edip yeniden deneyin.',
  https_required: 'Bu istek yalnız güvenli bağlantı üzerinden yapılabilir.',
  route_not_found: 'İstenen adres bulunamadı.',
  internal_error: 'Sunucuda beklenmeyen bir hata oldu. Sorun sürerse referans kodunu iletin.',
};

const KIND_FALLBACK: Record<ApiErrorKind, string> = {
  network: 'Sunucuya ulaşılamadı. Bağlantınızı kontrol edip yeniden deneyin.',
  malformed: 'İstek kabul edilmedi. Gönderilen bilgileri kontrol edin.',
  unauthenticated: 'Oturumunuz bulunamadı. Yeniden giriş yapın.',
  unauthorized: 'Bu işlem için yetkiniz yok.',
  not_found: 'Kayıt bulunamadı.',
  conflict: 'İşlem bu durumda yapılamaz.',
  gone: 'Bu bağlantı artık geçerli değil.',
  validation: 'Gönderilen bilgiler doğrulanamadı.',
  rate_limited: 'Çok fazla istek gönderildi. Kısa bir süre sonra yeniden deneyin.',
  server: 'Sunucuda beklenmeyen bir hata oldu. Sorun sürerse referans kodunu iletin.',
};

/** Actionable, user-safe Turkish sentence for an error. */
export function userMessage(error: unknown): string {
  if (!isApiError(error)) {
    return 'Beklenmeyen bir hata oldu. Sayfayı yenileyip yeniden deneyin.';
  }
  return MESSAGES[error.code] ?? KIND_FALLBACK[error.kind];
}

/**
 * The form's own words for the request paths the backend's schema names.
 *
 * `error.details` says exactly which property was rejected — `subject.displayName`, `issueDate` —
 * and until now nothing showed it, so a refusal about ONE field reached the operator as «request
 * shape is invalid»: the backend's own words, English, and true of the whole request rather than
 * of the field that was wrong. These two tables turn a detail into the sentence the screen would
 * have written itself. A path with no entry is shown as itself rather than guessed at.
 */
const REQUEST_FIELD_NAMES: Record<string, string> = {
  'subject.displayName': 'Sahip/alıcı adı',
  'subject.contactEmail': 'İletişim e-postası',
  'subject.externalReference': 'Dış referans',
  templateReleaseId: 'Şablon sürümü',
  claims: 'Alan değerleri',
  issueDate: 'Veriliş tarihi',
  expiresAt: 'Geçerlilik',
  validFrom: 'Geçerlilik başlangıcı',
  validUntil: 'Geçerlilik bitişi',
  lang: 'Dil',
  sendEmail: 'E-posta gönder',
  recipientEmail: 'İletişim e-postası',
  reason: 'Gerekçe',
  reasonCode: 'Gerekçe türü',
  baseRevisionId: 'Düzeltilen revision',
  internalNote: 'İç not',
  /* The issuer profile's own fields (PO 7.11): the server checks these beyond their shape, so the
     refusal has a field to name. */
  issuerDisplayName: 'Veren kuruluş',
  linkedinIssuerName: 'LinkedIn kurum adı',
  publicBase: 'Public adres',
  issuerWebsite: 'Web sitesi',
  linkedinPageUrl: 'LinkedIn sayfası',
  supportEmail: 'Destek e-postası',
  /* The template's own optional LinkedIn issuing organisation: refused per field, so a server
     refusal that reaches a toast still says which of the two boxes is wrong. */
  'linkedInIssuer.organizationName': "LinkedIn'de veren kuruluş adı",
  'linkedInIssuer.pageUrl': "LinkedIn'de veren kuruluşun sayfası",
};

/** The schema keyword, as a reason a person can act on. */
const REQUEST_FIELD_REASONS: Record<string, string> = {
  required: 'gönderilmedi',
  minLength: 'boş bırakılamaz',
  maxLength: 'çok uzun',
  pattern: 'biçimi geçersiz',
  format: 'biçimi geçersiz',
  type: 'türü geçersiz',
  enum: 'tanınmayan bir değer taşıyor',
  additionalProperties: 'tanınmıyor',
  /* Beyond the schema's vocabulary: what the server itself decided about the value (PO 7.11). */
  blank: 'yalnız boşluk içeremez',
  too_long: 'çok uzun',
  url_invalid: 'geçerli bir adres değil',
  url_scheme_unsupported: 'http veya https ile başlamalı',
  url_requires_https: 'https ile başlamalı',
  url_carries_credentials: 'kullanıcı adı veya parola taşıyamaz',
  url_host_missing: 'bir alan adı içermeli',
  url_host_not_qualified: 'tam bir alan adı içermeli',
  url_carries_query_or_fragment: 'sorgu dizesi veya fragment taşıyamaz',
  email_invalid: 'geçerli bir e-posta adresi değil',
};

const fieldName = (key: string): string => REQUEST_FIELD_NAMES[key] ?? (key === '$' ? 'İstek' : key);

/**
 * The rejected fields, named, or `null` when the answer carried none.
 *
 * Deliberately short: one clause per field, no schema vocabulary, and nothing from the provider.
 */
export function fieldIssueSentence(error: unknown): string | null {
  if (!isApiError(error) || error.details.length === 0) return null;
  const seen = new Set<string>();
  const parts: string[] = [];
  for (const d of error.details) {
    const key = d.key || '$';
    if (seen.has(key)) continue;
    seen.add(key);
    parts.push(`${fieldName(key)} — ${REQUEST_FIELD_REASONS[d.code] ?? 'geçersiz'}`);
  }
  return parts.length === 1
    ? `Sunucu şu alanı kabul etmedi: ${parts[0]}.`
    : `Sunucu şu alanları kabul etmedi: ${parts.join('; ')}.`;
}

/** Field-level issues keyed by field name, for form binding. */
export function issuesByKey(error: unknown): Record<string, string> {
  if (!isApiError(error)) return {};
  const out: Record<string, string> = {};
  for (const d of error.details) {
    const key = d.key || '$';
    if (!out[key]) out[key] = d.message || d.code;
  }
  return out;
}

/** `code · corr_… ` suffix a screen may show next to the message so support can trace it. */
export function errorReference(error: unknown): string | null {
  if (!isApiError(error)) return null;
  const parts = [error.code];
  if (error.correlationId) parts.push(error.correlationId);
  return parts.join(' · ');
}
